import React from 'react'
import { Button, Card, Form, Grid, Input, Modal, Popconfirm, Select, Space, Switch, Table, Typography, message } from 'antd'
import { listServices, createService, updateService, deleteService, listServiceLocationMaps, createServiceLocationMap, updateServiceLocationMap, deleteServiceLocationMap } from '../../api/services'
import { listDeliveryLocations } from '../../api/deliveryLocations'

const { useBreakpoint } = Grid

export default function ServicesAdmin() {
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const [services, setServices] = React.useState([])
  const [maps, setMaps] = React.useState([])
  const [locations, setLocations] = React.useState([])
  const [loading, setLoading] = React.useState(false)

  const [serviceCreateOpen, setServiceCreateOpen] = React.useState(false)
  const [serviceCreating, setServiceCreating] = React.useState(false)
  const [serviceEditOpen, setServiceEditOpen] = React.useState(false)
  const [serviceEditing, setServiceEditing] = React.useState(false)
  const [selectedService, setSelectedService] = React.useState(null)

  const [mapCreateOpen, setMapCreateOpen] = React.useState(false)
  const [mapCreating, setMapCreating] = React.useState(false)
  const [mapEditOpen, setMapEditOpen] = React.useState(false)
  const [mapEditing, setMapEditing] = React.useState(false)
  const [selectedMap, setSelectedMap] = React.useState(null)
  const [locationFilter, setLocationFilter] = React.useState(null)

  const [serviceCreateForm] = Form.useForm()
  const [serviceEditForm] = Form.useForm()
  const [mapCreateForm] = Form.useForm()
  const [mapEditForm] = Form.useForm()

  const fetchData = React.useCallback(async () => {
    setLoading(true)
    try {
      const [svcList, mapsList, locList] = await Promise.all([
        listServices(),
        listServiceLocationMaps(locationFilter ?? undefined),
        listDeliveryLocations(),
      ])
      setServices(svcList || [])
      setMaps(mapsList || [])
      setLocations(locList || [])
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setLoading(false)
    }
  }, [locationFilter])

  React.useEffect(() => { fetchData() }, [fetchData])

  function parseError(e) {
    const t = (e && e.message) ? e.message : 'Request failed'
    return t.replace(/^[\s"']+|[\s"']+$/g, '')
  }

  // ---------- Services CRUD ----------
  async function onServiceCreate(values) {
    setServiceCreating(true)
    try {
      await createService({
        serviceKey: values.serviceKey?.trim(),
        nameAr: values.nameAr?.trim(),
      })
      message.success('تم الإنشاء')
      setServiceCreateOpen(false)
      serviceCreateForm.resetFields()
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setServiceCreating(false)
    }
  }

  function openServiceEdit(record) {
    setSelectedService(record)
    serviceEditForm.setFieldsValue({ nameAr: record.nameAr })
    setServiceEditOpen(true)
  }

  async function onServiceEdit(values) {
    if (!selectedService) return
    setServiceEditing(true)
    try {
      await updateService(selectedService.id, {
        nameAr: values.nameAr?.trim(),
      })
      message.success('تم التحديث')
      setServiceEditOpen(false)
      setSelectedService(null)
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setServiceEditing(false)
    }
  }

  async function onServiceDelete(id) {
    try {
      await deleteService(id)
      message.success('تم الحذف')
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    }
  }

  // ---------- Service-Location Map CRUD ----------
  async function onMapCreate(values) {
    setMapCreating(true)
    try {
      await createServiceLocationMap({
        serviceId: values.serviceId,
        deliveryLocationId: values.deliveryLocationId,
        allowed: values.allowed !== false,
        disableReason: values.disableReason?.trim() || undefined,
      })
      message.success('تم الإنشاء')
      setMapCreateOpen(false)
      mapCreateForm.resetFields()
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setMapCreating(false)
    }
  }

  function openMapEdit(record) {
    setSelectedMap(record)
    mapEditForm.setFieldsValue({
      allowed: record.allowed,
      disableReason: record.disableReason || '',
    })
    setMapEditOpen(true)
  }

  async function onMapEdit(values) {
    if (!selectedMap) return
    setMapEditing(true)
    try {
      await updateServiceLocationMap(selectedMap.id, {
        allowed: values.allowed,
        disableReason: values.disableReason?.trim() || undefined,
      })
      message.success('تم التحديث')
      setMapEditOpen(false)
      setSelectedMap(null)
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setMapEditing(false)
    }
  }

  async function onMapDelete(id) {
    try {
      await deleteServiceLocationMap(id)
      message.success('تم الحذف')
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    }
  }

  const serviceColumns = [
    { title: 'رقم', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'المفتاح', dataIndex: 'serviceKey', key: 'serviceKey', width: 180 },
    { title: 'الاسم', dataIndex: 'nameAr', key: 'nameAr' },
    {
      title: '',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button size="small" onClick={() => openServiceEdit(record)}>تعديل</Button>
          <Popconfirm title="حذف هذه الخدمة؟" onConfirm={() => onServiceDelete(record.id)}>
            <Button size="small" danger>حذف</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const mapColumns = [
    { title: 'الخدمة', key: 'service', render: (_, r) => `${r.serviceNameAr || ''} (${r.serviceKey || ''})` },
    { title: 'المنطقة', key: 'location', render: (_, r) => r.deliveryLocationNameAr || '' },
    { title: 'مفعّل', dataIndex: 'allowed', key: 'allowed', width: 80, render: (v) => (v ? 'نعم' : 'لا') },
    { title: 'سبب التعطيل', dataIndex: 'disableReason', key: 'disableReason', render: (v) => v || '—' },
    {
      title: '',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button size="small" onClick={() => openMapEdit(record)}>تعديل</Button>
          <Popconfirm title="حذف هذا الربط؟" onConfirm={() => onMapDelete(record.id)}>
            <Button size="small" danger>حذف</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const modalMobileProps = isMobile
    ? {
        width: '95vw',
        style: { top: 12, paddingBottom: 0 },
        styles: { body: { maxHeight: '70vh', overflowY: 'auto' } },
        centered: false,
      }
    : { centered: true }

  return (
    <div
      dir="rtl"
      style={{
        maxWidth: 1000,
        margin: isMobile ? '12px auto' : '40px auto',
        padding: isMobile ? '0 8px' : '0 16px',
      }}
    >
      <Typography.Title level={isMobile ? 4 : 3} style={{ marginBottom: isMobile ? 12 : 24 }}>
        الخدمات وربط المناطق
      </Typography.Title>

      {/* Services */}
      <Card
        title="الخدمات"
        style={{ marginBottom: isMobile ? 12 : 24 }}
        styles={{ body: { padding: isMobile ? 8 : 24 } }}
      >
        <Space style={{ marginBottom: 12 }} wrap size="small">
          <Button
            type="primary"
            onClick={() => setServiceCreateOpen(true)}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? 'إضافة' : 'إضافة خدمة'}
          </Button>
        </Space>
        <Table
          rowKey="id"
          columns={serviceColumns}
          dataSource={services}
          loading={loading}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: isMobile ? 8 : 10, simple: isMobile }}
        />
      </Card>

      {/* Service-Location Maps */}
      <Card
        title="ربط الخدمات بالمناطق"
        styles={{ body: { padding: isMobile ? 8 : 24 } }}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12, fontSize: isMobile ? 12 : undefined }}>
          إذا لم تكن الخدمة مربوطة بمنطقة، تظهر للمستخدم كـ &quot;قريباً&quot;. الربط يفعّل الخدمة في المنطقة. يمكن تعطيلها بـ &quot;سبب التعطيل&quot;.
        </Typography.Paragraph>
        <Space
          style={{ marginBottom: 12, width: isMobile ? '100%' : undefined }}
          wrap
          size="small"
          direction={isMobile ? 'vertical' : 'horizontal'}
        >
          <Button
            type="primary"
            onClick={() => setMapCreateOpen(true)}
            size={isMobile ? 'small' : 'middle'}
            block={isMobile}
          >
            {isMobile ? 'إضافة ربط' : 'إضافة ربط'}
          </Button>
          <Select
            placeholder="تصفية بالمنطقة"
            allowClear
            size={isMobile ? 'small' : 'middle'}
            style={{ width: isMobile ? '100%' : 200 }}
            value={locationFilter}
            onChange={setLocationFilter}
            options={locations.map(l => ({ label: l.nameAr, value: l.id }))}
          />
        </Space>
        <Table
          rowKey="id"
          columns={mapColumns}
          dataSource={maps}
          loading={loading}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: isMobile ? 8 : 10, simple: isMobile }}
        />
      </Card>

      {/* Service Create Modal */}
      <Modal
        title="إضافة خدمة"
        open={serviceCreateOpen}
        onCancel={() => setServiceCreateOpen(false)}
        okText="إضافة"
        confirmLoading={serviceCreating}
        onOk={() => serviceCreateForm.submit()}
        {...modalMobileProps}
      >
        <Form form={serviceCreateForm} layout="vertical" onFinish={onServiceCreate}>
          <Form.Item label="المفتاح (إنجليزي)" name="serviceKey" rules={[{ required: true, message: 'مطلوب' }]}>
            <Input placeholder="CHARGE_ELECTRICITY" />
          </Form.Item>
          <Form.Item label="الاسم بالعربية" name="nameAr" rules={[{ required: true, message: 'مطلوب' }]}>
            <Input placeholder="شحن كهرباء" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Service Edit Modal */}
      <Modal
        title="تعديل الخدمة"
        open={serviceEditOpen}
        onCancel={() => setServiceEditOpen(false)}
        okText="حفظ"
        confirmLoading={serviceEditing}
        onOk={() => serviceEditForm.submit()}
        {...modalMobileProps}
      >
        <Form form={serviceEditForm} layout="vertical" onFinish={onServiceEdit}>
          <Form.Item label="الاسم بالعربية" name="nameAr" rules={[{ required: true, message: 'مطلوب' }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* Map Create Modal */}
      <Modal
        title="إضافة ربط خدمة بمنطقة"
        open={mapCreateOpen}
        onCancel={() => setMapCreateOpen(false)}
        okText="إضافة"
        confirmLoading={mapCreating}
        onOk={() => mapCreateForm.submit()}
        {...modalMobileProps}
      >
        <Form form={mapCreateForm} layout="vertical" onFinish={onMapCreate}>
          <Form.Item label="الخدمة" name="serviceId" rules={[{ required: true, message: 'مطلوب' }]}>
            <Select
              placeholder="اختر الخدمة"
              showSearch
              optionFilterProp="label"
              options={services.map(s => ({ label: `${s.nameAr} (${s.serviceKey})`, value: s.id }))}
            />
          </Form.Item>
          <Form.Item label="المنطقة" name="deliveryLocationId" rules={[{ required: true, message: 'مطلوب' }]}>
            <Select
              placeholder="اختر المنطقة"
              showSearch
              optionFilterProp="label"
              options={locations.map(l => ({ label: l.nameAr, value: l.id }))}
            />
          </Form.Item>
          <Form.Item label="مفعّل" name="allowed" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item label="سبب التعطيل (اختياري)" name="disableReason">
            <Input placeholder="مقطوع حالياً / قريباً..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Map Edit Modal */}
      <Modal
        title="تعديل الربط"
        open={mapEditOpen}
        onCancel={() => setMapEditOpen(false)}
        okText="حفظ"
        confirmLoading={mapEditing}
        onOk={() => mapEditForm.submit()}
        {...modalMobileProps}
      >
        <Form form={mapEditForm} layout="vertical" onFinish={onMapEdit}>
          <Form.Item label="مفعّل" name="allowed" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="سبب التعطيل (اختياري)" name="disableReason">
            <Input placeholder="مقطوع حالياً..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
